import React from 'react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { ServiceList } from '../components/ui/ServiceGroups';
import { CtaBand } from '../components/ui/CtaBand';
import { Faq } from '../components/ui/Faq';
import { FACILITY_UNITS, IMAGES } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const FAQS = [{
  question: '¿Se puede contratar Facility Management de forma integral?',
  answer: 'Sí. Limpieza, mantenimiento técnico, control de plagas, parquización y poda en altura pueden gestionarse bajo un mismo contrato, con un responsable único y un plan de servicio por instalación.'
}, {
  question: '¿El personal técnico está matriculado?',
  answer: 'El personal es capacitado y matriculado cuando la actividad lo requiere, en especial en trabajos de electricidad, gas y tareas con exigencias normativas específicas.'
}, {
  question: '¿Se trabaja fuera del horario de la empresa?',
  answer: 'Sí. Los horarios se adaptan a la operación del cliente para no interferir con la actividad productiva o administrativa.'
}, {
  question: '¿Existen alternativas ecológicas de tratamiento?',
  answer: 'Sí. Hay disponibilidad de productos sustentables en limpieza y de una línea de tratamiento ecológica en control de plagas cuando la aplicación lo permite.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Soluciones',
  path: '/soluciones'
}, {
  name: 'Facility Management',
  path: '/soluciones/facility-management'
}];
export function FacilityManagement() {
  useSeo({
    title: 'Facility Management Argentina | Limpieza y mantenimiento industrial — Rabemar',
    description: 'Facility Management integral en Pergamino y Argentina: limpieza corporativa e industrial, mantenimiento técnico, control de plagas, parquización y poda en altura.',
    path: '/soluciones/facility-management',
    image: IMAGES.facility,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('Facility Management integral', 'Limpieza corporativa e industrial, mantenimiento técnico preventivo y correctivo, control de plagas, parquización y poda en altura.', '/soluciones/facility-management'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="04 — Facility Management" title="Todo lo que mantiene funcionando una instalación." lead="Integramos mantenimiento, limpieza, servicios técnicos y espacios exteriores dentro de una única gestión, con estándares definidos y control de cumplimiento." image={IMAGES.facility} imageAlt="Operaria de limpieza profesional utilizando una fregadora industrial en un hall corporativo" crumbs={CRUMBS} ctaLabel="Cotizar Facility Management" ctaService="Facility Management" stats={[{
      value: '4 líneas',
      label: 'Limpieza, mantenimiento, plagas y espacios verdes'
    }, {
      value: 'Un contrato',
      label: 'Una sola gestión para toda la instalación'
    }, {
      value: 'Personal matriculado',
      label: 'Cuando la actividad lo requiere'
    }, {
      value: 'Horarios adaptados',
      label: 'Sin interferir con la operación'
    }]} data-id="element-636" data-mp="10plrgq48w12z" />

      <section className="bg-white py-20 md:py-28" data-id="element-637" data-mp="2ydz2vtg6bfsp">
        <Container data-id="element-638" data-mp="2yguznegabyfk">
          <div className="max-w-3xl" data-id="element-639" data-mp="20ri8avytrgan">
            <Eyebrow data-id="element-640" data-mp="20sjtht9k1j63">Unidades de servicio</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-641" data-mp="25qcxcfndy137">
              Cuatro líneas, un solo responsable.
            </Heading>
          </div>

          <div className="mt-14 space-y-16" data-id="element-642" data-mp="2aomxz5rvptw7">
            {FACILITY_UNITS.map((unit, i) => <article key={unit.title} className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-14" data-id="element-643" data-mp="2fpalhhgwfslo" data-mpi={i}>
                <div className={i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-644" data-mp="3m3tey8so50bw">
                  <span className="font-display text-sm font-bold tabular-nums text-accent-500" data-id="element-645" data-mp="3r36arj1iphqm">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-graphite-900 md:text-3xl" data-id="element-646" data-mp="3knflrcwpln6b">
                    {unit.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-graphite-500" data-id="element-647" data-mp="2j4b2zolt2ul">{unit.intro}</p>
                  <div className="mt-7" data-id="element-648" data-mp="3tmsyceg4qrv9">
                    <ServiceList items={unit.items} data-id="element-649" data-mp="bi9821kioo4n" />
                  </div>
                </div>
                <div className={`relative min-h-[280px] overflow-hidden bg-mist-100 ${i % 2 === 1 ? 'lg:order-1' : ''}`} data-id="element-650" data-mp="7h5p3676abz7">
                  <img src={unit.image} alt={`${unit.title} — equipo de Rabemar en operación`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" data-id="element-651" data-mp="l797fm2xlljd" />
                </div>
              </article>)}
          </div>
        </Container>
      </section>

      <section className="bg-brand-900 py-20 md:py-28" data-id="element-652" data-mp="19tgo5wlpl6e4">
        <Container data-id="element-653" data-mp="19tyr1c2vmiv0">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16" data-id="element-654" data-mp="19rcmllba8jh0">
            <div data-id="element-655" data-mp="1dtzhxw75wmiy">
              <Eyebrow tone="dark" data-id="element-656" data-mp="1dsg0w3dom7g4">Poda en altura</Eyebrow>
              <Heading as="h2" size="md" tone="dark" className="mt-5" data-id="element-657" data-mp="zu7wjaebg3ux">
                Árboles bajo control, riesgo bajo control.
              </Heading>
              <p className="mt-6 text-base leading-relaxed text-mist-200" data-id="element-658" data-mp="q57xxs3q7ah9">
                Trabajos ejecutados con personal capacitado y protocolos de seguridad, orientados a prevenir la caída de
                ramas, sanear el árbol, favorecer su crecimiento y evitar interferencias con edificios, cables e
                infraestructura.
              </p>
            </div>
            <div className="grid gap-px bg-white/10 sm:grid-cols-2" data-id="element-659" data-mp="m1ia54uvkwxa">
              {['Prevención de caída de ramas', 'Saneamiento del árbol', 'Crecimiento saludable', 'Mejora de iluminación y ventilación', 'Control de tamaño', 'Sin interferencia con infraestructura'].map(item => <p key={item} className="bg-brand-900 px-5 py-4 text-sm text-mist-200" data-id="element-660" data-mp="3kdek5inba7cy">
                  {item}
                </p>)}
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-661" data-mp="bzo1ga0djtb6">
        <Container data-id="element-662" data-mp="byd16k6513ot">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-663" data-mp="bywetoor95x1">
            <div data-id="element-664" data-mp="71w7zbk2zz4v">
              <Eyebrow data-id="element-665" data-mp="71cv499ag0xp">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-666" data-mp="20p3pmh5di2y">
                Facility Management
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-667" data-mp="3t79sezjqcdwt" />
          </div>
        </Container>
      </section>

      <CtaBand title="Cotice su Facility Management completo." text="Relevamos la instalación, definimos frecuencias, estándares y personal necesario, y entregamos una propuesta por unidad o integral." primaryLabel="Cotizar Facility Management" location="facility-cta" service="Facility Management" data-id="element-668" data-mp="2msiiy7gjjkr6" />
    </>;
}